import { useCallback, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtomValue } from "jotai";
import { Input } from "@common/Form/Input";
import { Button } from "@common/Button";
import { ComponentColor } from "@common/types";
import { US_STATES } from "@utils/usStates";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { isProcessingLocationsAtom } from "@/store/locationAtoms";
import type { LocationResponse } from "@/models/location";
import { wasReported } from "@utils/apiFailure";
import {
    createLocationSchema,
    editLocationSchema,
    type LocationFormData,
} from "../_helpers/locationFormSchemas";

export const LOCATION_FORM_MODAL_ID = "accountLocationFormModal";

const EMPTY_FORM: LocationFormData = {
    name: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
};

interface LocationFormModalProps {
    /** When set, the modal edits this location (name-only); otherwise it creates. */
    location?: LocationResponse;
    /** Organization the new location belongs to. Required for create. */
    organizationId?: string;
    /** After a successful save; a create passes the new location's id. */
    onSaved: (createdLocationId?: string) => void;
    onClose?: () => void;
}

const closeDialog = () => {
    const modal = document.getElementById(
        LOCATION_FORM_MODAL_ID,
    ) as HTMLDialogElement | null;
    modal?.close();
};

export const LocationFormModal = ({
    location,
    organizationId,
    onSaved,
    onClose,
}: LocationFormModalProps) => {
    const { createLocation, updateLocation } = useLocationApi();
    const isProcessing = useAtomValue(isProcessingLocationsAtom);
    const isEdit = !!location;

    const methods = useForm<LocationFormData>({
        // Same `isEdit` that disables the address fields picks the rules for
        // them — one source of truth. This modal is a persistent instance
        // (AccountLocations mounts one and flips it between add and edit),
        // and react-hook-form re-reads _options on every render, so the swap
        // follows a mode change.
        resolver: zodResolver(isEdit ? editLocationSchema : createLocationSchema),
        defaultValues: EMPTY_FORM,
    });

    const {
        register,
        reset,
        formState: { errors },
    } = methods;

    // Sync the form to the selected row whenever it changes (pre-fill on edit).
    useEffect(() => {
        reset(
            location
                ? {
                    name: location.name,
                    streetAddress: location.streetAddress,
                    city: location.city,
                    state: location.state,
                    zipCode: location.zipCode,
                    country: location.country,
                }
                : EMPTY_FORM,
        );
    }, [location, reset]);

    const handleClose = useCallback(() => {
        closeDialog();
        onClose?.();
    }, [onClose]);

    const onSubmit = async (data: LocationFormData) => {
        let createdLocationId: string | undefined;
        try {
            if (isEdit) {
                await updateLocation(location.id, { name: data.name });
            } else {
                if (!organizationId) return;
                const created = await createLocation(organizationId, data);
                createdLocationId = created.id;
            }
        } catch (error) {
            // useApi has shown this one: the form stays open with what was
            // typed. Any other failure was shown to nobody, so it escapes
            // (react-hook-form rethrows it out of the submit).
            if (wasReported(error)) return;
            throw error;
        }
        onSaved(createdLocationId);
        handleClose();
    };

    return (
        <dialog
            id={LOCATION_FORM_MODAL_ID}
            className="modal text-neutral"
            onCancel={(e) => {
                if (isProcessing) {
                    e.preventDefault();
                    return;
                }
                // Esc closes the dialog natively; clear the parent's selected-row state too.
                onClose?.();
            }}
        >
            <div className="modal-box max-w-lg">
                <h1 className="font-bold text-2xl mb-4">
                    {isEdit ? "Edit Location" : "Add Location"}
                </h1>
                <FormProvider {...methods}>
                    <form
                        onSubmit={methods.handleSubmit(onSubmit)}
                        className="flex flex-col gap-3"
                    >
                        <Input
                            name="name"
                            placeholder="Location Name"
                            inputClass="text-black"
                        />
                        <Input
                            name="streetAddress"
                            placeholder="Street Address"
                            inputClass="text-black"
                            disabled={isEdit}
                        />
                        <Input
                            name="city"
                            placeholder="City"
                            inputClass="text-black"
                            disabled={isEdit}
                        />
                        {/* A select, not a text input: the submitted value has
                            to be the 2-letter USPS code for the legal-class
                            restrictions to match at all. The other fields name
                            themselves through their placeholder, so this one
                            carries an aria-label instead of a visible label.
                            Not @common/Form/Select: SelectProps has no
                            aria-label and nothing is spread onto its <select>,
                            so labelText — which renders a VISIBLE label — is
                            its only accessible-name path. */}
                        <div className="w-full">
                            <select
                                id="state"
                                aria-label="State"
                                className="select select-bordered w-full bg-white text-neutral"
                                disabled={isEdit}
                                {...register("state")}
                            >
                                <option value="">Select a state</option>
                                {US_STATES.map((state) => (
                                    <option key={state.code} value={state.code}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                            {errors.state && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.state.message}
                                </p>
                            )}
                        </div>
                        <Input
                            name="zipCode"
                            placeholder="Zip Code"
                            inputClass="text-black"
                            disabled={isEdit}
                        />
                        <Input
                            name="country"
                            placeholder="Country"
                            inputClass="text-black"
                            disabled={isEdit}
                        />

                        {isEdit && (
                            <p className="text-sm text-neutral/70">
                                Only the location name can be changed right now.
                            </p>
                        )}

                        <div className="modal-action">
                            <Button
                                color={ComponentColor.Primary}
                                type="submit"
                                loading={isProcessing}
                            >
                                {isProcessing ? "Saving..." : "Save"}
                            </Button>
                            <Button
                                color={ComponentColor.Neutral}
                                disabled={isProcessing}
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </div>
            {!isProcessing && (
                <form method="dialog" className="modal-backdrop">
                    <button onClick={handleClose}>close</button>
                </form>
            )}
        </dialog>
    );
};
